import { z } from 'zod';
import { assertLocalDay } from './dates';
import { INT64_MAX, INT64_MIN } from './money';
import {
  ENTITY_TYPES,
  type Account,
  type Adjustment,
  type AnyEntity,
  type AppSettings,
  type Category,
  type EntityType,
  type Expense,
  type Income,
  type IncomeSource,
  type LedgerSnapshot,
  type Payment,
  type RecurrenceException,
  type RecurrenceRule,
  type Transfer
} from './model';
import { paidAmount } from './ledger';

export class LedgerValidationError extends Error {
  constructor(public readonly issues: string[]) {
    super(issues[0] ?? 'O conjunto de dados é inválido.');
    this.name = 'LedgerValidationError';
  }
}

export const entityTypeSchema = z.enum(ENTITY_TYPES);
export const uuidSchema = z.string().uuid();
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const localDaySchema = z.string().refine((value) => {
  try { assertLocalDay(value); return true; } catch { return false; }
}, 'Data inválida.');

const colorPattern = /^#?[0-9a-f]{6}$/i;
const nonEmpty = (value: unknown, maximum = 500) => typeof value === 'string' && value.trim().length > 0 && value.length <= maximum;
const boundedText = (value: unknown, maximum = 2_000) => typeof value === 'string' && value.length <= maximum;
const optionalText = (value: unknown, maximum = 2_000) => value === undefined || (typeof value === 'string' && value.length <= maximum);
const validUUID = (value: unknown) => typeof value === 'string' && uuidSchema.safeParse(value).success;
const validTimestamp = (value: unknown) => typeof value === 'string' && isoDateTimeSchema.safeParse(value).success;
const validDay = (value: unknown) => typeof value === 'string' && localDaySchema.safeParse(value).success;
const oneOf = <T extends string>(value: unknown, values: readonly T[]): value is T => typeof value === 'string' && values.includes(value as T);

export function validateEntity(type: EntityType, entity: AnyEntity): void {
  const issues: string[] = [];
  if (!validUUID(entity.id)) issues.push(`${type}: identificador inválido.`);
  if (!validTimestamp(entity.createdAt) || !validTimestamp(entity.updatedAt) || (entity.deletedAt !== undefined && !validTimestamp(entity.deletedAt))) issues.push(`${type}: timestamp inválido.`);
  if (!Number.isInteger(entity.version) || entity.version < 0) issues.push(`${type}: versão inválida.`);
  for (const value of Object.values(entity)) {
    if (typeof value === 'bigint' && (value < INT64_MIN || value > INT64_MAX)) issues.push(`${type}: valor fora do intervalo Int64.`);
  }
  if ('amount' in entity && typeof entity.amount === 'bigint' && entity.amount <= 0n && !(type === 'adjustment')) issues.push(`${type}: o valor deve ser positivo.`);

  switch (type) {
    case 'account': {
      const record = entity as Account;
      if (!nonEmpty(record.name, 120) || !oneOf(record.kind, ['bank', 'cash']) || !nonEmpty(record.iconName, 80) || typeof record.colorHex !== 'string' || !colorPattern.test(record.colorHex) || typeof record.isArchived !== 'boolean') issues.push('Conta: campos inválidos.');
      break;
    }
    case 'incomeSource': {
      const record = entity as IncomeSource;
      if (!nonEmpty(record.name, 120) || !colorPattern.test(record.colorHex) || typeof record.isArchived !== 'boolean' || (record.mergedIntoID !== undefined && !validUUID(record.mergedIntoID))) issues.push('Origem: campos inválidos.');
      break;
    }
    case 'category': {
      const record = entity as Category;
      if (!nonEmpty(record.name, 120) || !oneOf(record.purpose, ['personal', 'company']) || !nonEmpty(record.iconName, 80) || typeof record.isArchived !== 'boolean' || (record.parentID !== undefined && !validUUID(record.parentID)) || (record.mergedIntoID !== undefined && !validUUID(record.mergedIntoID))) issues.push('Categoria: campos inválidos.');
      break;
    }
    case 'income': {
      const record = entity as Income;
      if (typeof record.amount !== 'bigint' || !validUUID(record.accountID) || !validUUID(record.sourceID) || !validTimestamp(record.receivedAt) || !nonEmpty(record.reference, 240) || !boundedText(record.note)) issues.push('Entrada: campos inválidos.');
      break;
    }
    case 'expense': {
      const record = entity as Expense;
      if (typeof record.amount !== 'bigint' || !oneOf(record.purpose, ['personal', 'company']) || !validUUID(record.categoryID) || !nonEmpty(record.merchant, 240) || !validDay(record.purchasedOn) || (record.dueOn !== undefined && !validDay(record.dueOn)) || (record.recurrenceRuleID !== undefined && !validUUID(record.recurrenceRuleID)) || !optionalText(record.logicalOccurrenceKey, 300) || !boundedText(record.note) || (record.canceledAt !== undefined && !validTimestamp(record.canceledAt)) || !optionalText(record.cancellationReason, 500)) issues.push('Saída: campos inválidos.');
      break;
    }
    case 'payment': {
      const record = entity as Payment;
      if (typeof record.amount !== 'bigint' || !validUUID(record.expenseID) || !validUUID(record.accountID) || !validTimestamp(record.paidAt)) issues.push('Pagamento: campos inválidos.');
      break;
    }
    case 'transfer': {
      const record = entity as Transfer;
      if (typeof record.amount !== 'bigint' || !validUUID(record.sourceAccountID) || !validUUID(record.destinationAccountID) || !validTimestamp(record.occurredAt) || !boundedText(record.note)) issues.push('Transferência: campos inválidos.');
      if (record.sourceAccountID === record.destinationAccountID) issues.push('Transferência: origem e destino devem ser diferentes.');
      break;
    }
    case 'adjustment': {
      const record = entity as Adjustment;
      if (typeof record.amount !== 'bigint' || !validUUID(record.accountID) || !oneOf(record.kind, ['opening', 'correction']) || !validTimestamp(record.occurredAt) || !boundedText(record.note)) issues.push('Ajuste: campos inválidos.');
      break;
    }
    case 'recurrenceRule': {
      const record = entity as RecurrenceRule;
      if (!validDay(record.anchor) || !oneOf(record.cadence, ['weekly', 'monthly', 'yearly']) || !Number.isInteger(record.interval) || record.interval < 1 || (record.endOn !== undefined && !validDay(record.endOn)) || (record.occurrenceCount !== undefined && (!Number.isInteger(record.occurrenceCount) || record.occurrenceCount < 1 || record.occurrenceCount > 2_000)) || !oneOf(record.kind, ['subscription', 'installment']) || !oneOf(record.state, ['active', 'paused', 'ended', 'completed']) || typeof record.amount !== 'bigint' || !oneOf(record.purpose, ['personal', 'company']) || !validUUID(record.categoryID) || !nonEmpty(record.merchant, 240) || !boundedText(record.note) || (record.pausedOn !== undefined && !validDay(record.pausedOn)) || (record.endedOn !== undefined && !validDay(record.endedOn))) issues.push('Recorrência: campos inválidos.');
      break;
    }
    case 'recurrenceException': {
      const record = entity as RecurrenceException;
      if (!validUUID(record.recurrenceRuleID) || !validDay(record.startsOn) || !validDay(record.endsOn) || record.startsOn > record.endsOn || !oneOf(record.reason, ['deletedOccurrence', 'paused', 'ended', 'canceledInstallments'])) issues.push('Exceção de recorrência: campos inválidos.');
      break;
    }
    case 'appSettings': {
      const record = entity as AppSettings;
      if (!Array.isArray(record.dueDayTimes) || record.dueDayTimes.length !== 3 || record.dueDayTimes.some((time) => typeof time !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) || !Number.isInteger(record.schemaVersion) || record.schemaVersion < 1) issues.push('Ajustes: campos inválidos.');
      break;
    }
  }
  if (issues.length) throw new LedgerValidationError(issues);
}

export function validateLedger(snapshot: LedgerSnapshot): void {
  const issues: string[] = [];
  const active = <T extends { deletedAt?: string }>(records: T[]) => records.filter((record) => !record.deletedAt);
  const accounts = new Set(active(snapshot.accounts).map((record) => record.id));
  const sources = new Set(active(snapshot.incomeSources).map((record) => record.id));
  const categories = new Map(active(snapshot.categories).map((record) => [record.id, record]));
  const expenses = new Map(active(snapshot.expenses).map((record) => [record.id, record]));

  for (const type of ENTITY_TYPES) {
    const records = type === 'appSettings' ? [snapshot.settings] : (snapshot[({ account: 'accounts', incomeSource: 'incomeSources', category: 'categories', income: 'incomes', expense: 'expenses', payment: 'payments', transfer: 'transfers', adjustment: 'adjustments', recurrenceRule: 'recurrenceRules', recurrenceException: 'recurrenceExceptions' } as const)[type as Exclude<EntityType, 'appSettings'>]] as AnyEntity[]);
    for (const entity of records) {
      try { validateEntity(type, entity as AnyEntity); } catch (error) { if (error instanceof LedgerValidationError) issues.push(...error.issues); else throw error; }
    }
  }

  for (const category of categories.values()) {
    if (!category.name.trim()) issues.push('Categoria: nome obrigatório.');
    if (category.parentID) {
      const parent = categories.get(category.parentID);
      if (!parent || parent.parentID || parent.purpose !== category.purpose) issues.push(`Categoria ${category.name}: hierarquia inválida.`);
    }
  }
  for (const income of active(snapshot.incomes)) {
    if (!accounts.has(income.accountID) || !sources.has(income.sourceID)) issues.push('Entrada: conta ou origem inexistente.');
  }
  for (const expense of expenses.values()) {
    const category = categories.get(expense.categoryID);
    if (!category || category.purpose !== expense.purpose) issues.push('Saída: categoria incompatível.');
  }
  for (const payment of active(snapshot.payments)) {
    if (!accounts.has(payment.accountID) || !expenses.has(payment.expenseID)) issues.push('Pagamento: conta ou saída inexistente.');
  }
  for (const expense of expenses.values()) if (paidAmount(expense.id, snapshot.payments) > expense.amount) issues.push(`Saída ${expense.id}: pagamentos excedem o valor.`);
  for (const transfer of active(snapshot.transfers)) if (!accounts.has(transfer.sourceAccountID) || !accounts.has(transfer.destinationAccountID)) issues.push('Transferência: conta inexistente.');
  for (const adjustment of active(snapshot.adjustments)) if (!accounts.has(adjustment.accountID)) issues.push('Ajuste: conta inexistente.');
  for (const rule of active(snapshot.recurrenceRules)) {
    if (!categories.has(rule.categoryID) || rule.interval < 1 || (rule.kind === 'installment' && (rule.occurrenceCount === undefined || rule.occurrenceCount < 2 || rule.occurrenceCount > 600 || rule.amount < BigInt(rule.occurrenceCount)))) issues.push('Recorrência: configuração inválida.');
  }
  const rules = new Set(active(snapshot.recurrenceRules).map((record) => record.id));
  for (const exception of active(snapshot.recurrenceExceptions)) if (!rules.has(exception.recurrenceRuleID)) issues.push('Exceção de recorrência: série inexistente.');
  const occurrenceKeys = new Set<string>();
  for (const expense of active(snapshot.expenses)) {
    if (!expense.logicalOccurrenceKey) continue;
    if (occurrenceKeys.has(expense.logicalOccurrenceKey)) issues.push(`Ocorrência recorrente duplicada: ${expense.logicalOccurrenceKey}.`);
    occurrenceKeys.add(expense.logicalOccurrenceKey);
  }
  if (snapshot.settings.dueDayTimes.length !== 3 || snapshot.settings.dueDayTimes.some((time) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(time))) issues.push('Ajustes: horários de lembrete inválidos.');
  if (issues.length) throw new LedgerValidationError([...new Set(issues)]);
}
